import { Injectable } from '@angular/core';
import { Error as FoundationError } from 'fl-foundation';
import { NewrelicEvent, QPNewrelicService } from 'qp-newrelic';
import { ErrorInfo, ErrorSeverityType } from 'qp-store';
import { ErrorCodeTranslationService } from 'src/app/shared/services/errorcode-translation/errorcode-translation.service';
import { SessionService } from 'src/app/shared/services/session/session.service';
import { getPlayerErrorInfo } from 'src/app/shared/utils/playerUtils';
import { v4 as uuid } from 'uuid';
import { NewrelicError } from '../enums/errors';
import { StoreService } from '../store/store.service';

declare const newrelic: any;
declare const window: Window &
  typeof globalThis & {
    startTime: number;
  };

@Injectable({
  providedIn: 'root',
})
export class NewrelicService extends QPNewrelicService {
  constructor(
    readonly storeService: StoreService,
    private errorCodeService: ErrorCodeTranslationService,
    private sessionService: SessionService
  ) {
    // Last attribute is for showing NR logs
    super(storeService, newrelic, false);
  }

  public getTimeSinceLoadInfo(): { timeSinceLoad: number } {
    const timeNow = new Date().getTime();
    return {
      timeSinceLoad: window.startTime ? timeNow - window.startTime : 0,
    };
  }

  // Update timeSinceLoad and eventUUID before sending new event
  public sendEvent(eventName: NewrelicEvent): void {
    setTimeout(() => {
      this.store.setEventInfo({
        appName: 'UNow',
        eventUUID: uuid(),
        ...this.getTimeSinceLoadInfo(),
        session: this.sessionService.getSessionID(),
        // eventType: eventName, commenting as this was causing issue when reporting from Comcast devices.
      });
      return super.sendEvent(eventName);
    }, 0);
  }

  public async sendError(
    errorEvent: NewrelicError,
    error: FoundationError,
    ec: number,
    em: string,
    errorSeverity?: ErrorSeverityType
  ): Promise<void> {
    try {
      const errorDescription = (await this.errorCodeService.getErrorByCode(ec?.toString(), em)).message;
      const { errorCode, errorMessage } = getPlayerErrorInfo(error);
      this.sendErrorEvent(
        errorEvent,
        errorCode || ec?.toString(),
        errorMessage,
        errorDescription?.replace(/<br>/g, '\n'),
        error,
        errorSeverity || ErrorSeverityType.WARNING
      );
    } catch {}
  }

  sendErrorEvent(
    errorType: NewrelicError,
    errorCode: number | string,
    errorMessage: string,
    errorDescription: string,
    error: any,
    errorSeverity?: ErrorSeverityType,
    overrideAttributes?: any,
    internalError?: string
  ): void {
    setTimeout(() => {
      const errorAttributes = this.getEventAttributes(NewrelicEvent.ERROR);
      const errorInfo: ErrorInfo = {
        ...errorAttributes,
        ...(overrideAttributes || {}),
        errorCode: errorCode?.toString(),
        errorDescription,
        errorMessage,
        errorType,
        internalError,
        errorInternal: error,
        errorSeverity: errorSeverity || ErrorSeverityType.WARNING,
        eventUUID: uuid(),
      };
      this.sendBySDK(NewrelicEvent.ERROR, errorInfo);
    }, 0);
  }
}
